import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import CodeIcon from '@mui/icons-material/Code';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';

import { ExecutionHistorySelectDialog, WorkspaceFileSelectDialog } from 'components/training';
import { errorDefault } from 'utils/Toastify';

/** 編集中の期待値（由来＋プレビュー用の arguments/stdout） */
export type EditableExpected = {
  /** ローカル識別子（React key 用） */
  localId: string;
  sourceFileId: string;
  sourceSeq: number;
  /** プレビュー表示用（送信はしない・API はコピー済みか実行履歴から再取得する） */
  argumentsPreview: string | null;
  stdoutPreview: string | null;
};

/** 編集中の正解コード */
export type EditableAnswerCode = {
  localId: string;
  fileId: string;
  fileName: string;
  /** 正解コードファイルの絶対パス（knz 書き出し用。未取得は空） */
  filePath: string;
  extension: string;
  expectedList: EditableExpected[];
};

type Props = {
  answerCodes: EditableAnswerCode[];
  onChange: (answerCodes: EditableAnswerCode[]) => void;
};

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * 正解コード（言語ごと）＋期待値（実行履歴由来）の編集フォーム。
 * - 正解コード追加: ワークスペースの実行可能拡張子ファイルを選択（同一言語は1つまで）
 * - 期待値追加: その正解コードファイルの実行履歴を選択（引数・標準出力をプレビュー）
 */
const AnswerCodesEditor: React.FC<Props> = ({ answerCodes, onChange }) => {
  const [fileDialogOpen, setFileDialogOpen] = React.useState(false);
  const [historyDialog, setHistoryDialog] = React.useState<{ answerLocalId: string } | null>(null);

  const handleAddAnswerCode = (file: {
    fileId: string;
    name: string;
    extension: string;
    path: string;
  }) => {
    // 同一言語（拡張子）の重複は不可（D1-4）
    if (answerCodes.some((a) => a.extension === file.extension)) {
      errorDefault(
        `拡張子「${file.extension}」の正解コードは既に登録済みです（同一言語は1つまで）`,
      );
      setFileDialogOpen(false);
      return;
    }
    onChange([
      ...answerCodes,
      {
        localId: genId(),
        fileId: file.fileId,
        fileName: file.name,
        filePath: file.path,
        extension: file.extension,
        expectedList: [],
      },
    ]);
    setFileDialogOpen(false);
  };

  const handleRemoveAnswerCode = (localId: string) => {
    onChange(answerCodes.filter((a) => a.localId !== localId));
  };

  const handleAddExpected = (item: {
    seq: number;
    arguments: string | null;
    stdout: string | null;
  }) => {
    if (!historyDialog) return;
    const target = answerCodes.find((a) => a.localId === historyDialog.answerLocalId);
    if (!target) return;
    onChange(
      answerCodes.map((a) =>
        a.localId === historyDialog.answerLocalId
          ? {
              ...a,
              expectedList: [
                ...a.expectedList,
                {
                  localId: genId(),
                  sourceFileId: a.fileId,
                  sourceSeq: item.seq,
                  argumentsPreview: item.arguments,
                  stdoutPreview: item.stdout,
                },
              ],
            }
          : a,
      ),
    );
    setHistoryDialog(null);
  };

  const handleRemoveExpected = (answerLocalId: string, expectedLocalId: string) => {
    onChange(
      answerCodes.map((a) =>
        a.localId === answerLocalId
          ? { ...a, expectedList: a.expectedList.filter((e) => e.localId !== expectedLocalId) }
          : a,
      ),
    );
  };

  const historyTargetFileId =
    answerCodes.find((a) => a.localId === historyDialog?.answerLocalId)?.fileId ?? null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          正解コード・期待値
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setFileDialogOpen(true)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            borderStyle: 'dashed',
            '&:hover': { borderStyle: 'solid' },
          }}
        >
          正解コードを追加
        </Button>
      </Stack>

      {answerCodes.length === 0 ? (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            borderStyle: 'dashed',
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            正解コードを1つ以上追加してください（言語ごとに1つ・各正解コードに期待値1つ以上）
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {answerCodes.map((ac) => (
            <Box
              key={ac.localId}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
            >
              {/* 正解コードヘッダー */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 1.5, py: 1, bgcolor: 'action.hover' }}
              >
                <CodeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Chip size="small" label={ac.extension} sx={{ borderRadius: 1, fontWeight: 600 }} />
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                  {ac.fileName}
                </Typography>
                <IconButton size="small" onClick={() => handleRemoveAnswerCode(ac.localId)}>
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>

              {/* 期待値リスト */}
              <Box sx={{ p: 1.5 }}>
                {ac.expectedList.length === 0 ? (
                  <Typography variant="caption" color="error.main">
                    期待値を1つ以上追加してください
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {ac.expectedList.map((ex, idx) => (
                      <Box
                        key={ex.localId}
                        sx={{
                          borderLeft: 3,
                          borderColor: 'primary.main',
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          px: 1.5,
                          py: 1,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip
                            size="small"
                            label={`期待値 ${idx + 1}`}
                            variant="outlined"
                            sx={{ borderRadius: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                            由来: 実行 #{ex.sourceSeq}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveExpected(ac.localId, ex.localId)}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Stack>
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            引数: {ex.argumentsPreview ?? '(なし)'}
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              m: 0,
                              mt: 0.25,
                              p: 0.75,
                              borderRadius: 1,
                              bgcolor: 'background.paper',
                              border: 1,
                              borderColor: 'divider',
                              fontSize: 11,
                              maxHeight: 100,
                              overflow: 'auto',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                            }}
                          >
                            {ex.stdoutPreview ?? '(出力なし)'}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
                <Button
                  size="small"
                  startIcon={<PlaylistAddIcon />}
                  onClick={() => setHistoryDialog({ answerLocalId: ac.localId })}
                  sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                >
                  期待値を追加
                </Button>
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      <WorkspaceFileSelectDialog
        open={fileDialogOpen}
        onClose={() => setFileDialogOpen(false)}
        onSelect={handleAddAnswerCode}
        title="正解コードのファイルを選択"
        filter="runnable"
      />
      <ExecutionHistorySelectDialog
        open={historyDialog !== null}
        onClose={() => setHistoryDialog(null)}
        fileId={historyTargetFileId}
        onSelect={handleAddExpected}
        title="期待値の由来となる実行履歴を選択"
      />
    </Box>
  );
};

export default AnswerCodesEditor;
